import * as DialogPrimitive from '@radix-ui/react-dialog'
import React, { ReactNode, useEffect, useState } from 'react'

import { CSS, keyframes, styled } from '../stitches.config'
import { Button, ButtonProps } from './Button'
import { Icon } from './Icon'
import { ScrollArea } from './ScrollArea'
import { Text } from './Text'

export type ModalSizeTypes = 'xs' | 'small' | 'default' | 'large' | 'xLarge'

export interface ModalProps {
  /**
   * HTML element to open the modal.
   */
  openButton?: React.ReactNode

  /**
   * Short text to clarify a thought for the modal.
   */
  tagline?: string

  /**
   * The state of the modal.
   */
  open?: boolean

  /**
   * Title of the modal.
   */
  title?: string

  /**
   * Subordinate title of the modal.
   */
  subtitle?: string

  /**
   * Content for the modal.
   */
  children?: React.ReactNode

  /**
   * Disable the open button.
   */
  disabled?: boolean

  /**
   * The primary button's label.
   */
  primaryBtnLabel?: string

  /**
   * The secondary button's label.
   */
  secondaryBtnLabel?: string

  /**
   * The tertiary button's label.
   */
  tertiaryBtnLabel?: string

  /**
   * disabled primary button.
   */
  disablePrimaryBtn?: boolean

  /**
   * Size of the modal.
   */
  size?: ModalSizeTypes

  /**
   * Allows scroll the modal body.
   */
  scrollable?: boolean

  /**
   * The max height of the modal.
   */
  maxHeight?: number

  /**
   * Give css class name to modal component.
   */
  className?: string

  /**
   * Give css property to modal component.
   */
  css?: CSS

  /**
   * Get a callback when primary button is clicked.
   */
  primaryBtnCallback?: (e) => void | Promise<void | boolean>

  /**
   * Get a callback when secondary button is clicked.
   */
  secondaryBtnCallback?: (e) => void | Promise<void | boolean>

  /**
   * Get a callback when tertiary button is clicked.
   */
  tertiaryBtnCallback?: (e) => void | Promise<void | boolean>

  /**
   * Get a callback when the modal is opened or closed.
   */
  onOpenChange?: (open: boolean) => void
}

export const Modal: React.FC<ModalProps> = ({
  openButton = null,
  tagline = null,
  title = null,
  subtitle = null,
  children = null,
  size = 'default',
  scrollable = false,
  className = '',
  css = {},
  tertiaryBtnLabel,
  primaryBtnLabel = null,
  secondaryBtnLabel = null,
  primaryBtnCallback = null,
  secondaryBtnCallback = null,
  tertiaryBtnCallback = null,
  open = null,
  disabled = false,
  disablePrimaryBtn = false,
  onOpenChange = (_open: boolean) => {},
}) => {
  const [state, setState] = useState<boolean>(open !== null ? open : false)
  const [primaryBtnState, setPrimaryBtnState] = useState<ButtonProps['buttonState']>('default')
  const [secondaryBtnState, setSecondaryBtnState] = useState<ButtonProps['buttonState']>('default')

  const btnAttribute = { as: 'span' }

  useEffect(() => {
    if (open !== null) {
      setState(open)
    }
  }, [open])

  const handlePrimaryCallback = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()
    setPrimaryBtnState('waiting')
    if (primaryBtnCallback) {
      try {
        const res = await primaryBtnCallback(e)
        if (res === undefined || res === true) {
          onOpenChange(false)
          setState(false)
        }
      } catch (error) {
        console.error('error in primaryBtnCallback of Modal:', error)
      } finally {
        setPrimaryBtnState('default')
      }
    }
  }

  const handleSecondaryCallback = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.preventDefault()
    setSecondaryBtnState('waiting')
    if (secondaryBtnCallback) {
      try {
        const res = await secondaryBtnCallback(e)
        if (res === undefined || res === true) {
          onOpenChange(false)
          setState(false)
        }
      } catch (error) {
        console.error('error in secondaryBtnCallback of Modal:', error)
      } finally {
        setSecondaryBtnState('default')
      }
    }
  }

  const handleOpenChange = (state: boolean) => {
    setState(state)
    onOpenChange(state)
  }

  const tertiaryBtnVariant = secondaryBtnLabel && secondaryBtnLabel ? 'ghost' : 'tertiary'

  return (
    <ModalRoot onOpenChange={handleOpenChange} open={state}>
      <ModalTrigger disabled={disabled} asChild>
        {openButton}
      </ModalTrigger>
      <DialogPrimitive.Portal>
        <StyledOverlay />
        <ModalContainer css={css} className={className} size={size}>
          <ModalHeader as="div">
            <ModalHeaderContainer>
              {tagline && <Text variant="caption">{tagline}</Text>}
              <DialogPrimitive.DialogTitle>
                <Text css={{ color: '$gs12' }} variant="subtitle1">
                  {title}
                </Text>
              </DialogPrimitive.DialogTitle>
              <DialogPrimitive.Description>
                <Text oll={false} variant="body2">
                  {subtitle}
                </Text>
              </DialogPrimitive.Description>
            </ModalHeaderContainer>
            <ModalClose asChild>
              <Icon
                name="close"
                css={{
                  border: '1px solid $gs12',
                  borderRadius: '$3',
                  color: '$gs12',
                  '&:hover': {
                    opacity: '0.8',
                    transition: 'all $fast ease-in-out',
                  },
                }}
              />
            </ModalClose>
          </ModalHeader>
          {scrollable ? (
            <ScrollArea maxHeight={230}>
              <ModalContent>
                {React.Children.map<ReactNode, ReactNode>(children, child => {
                  if (React.isValidElement(child)) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    return React.cloneElement<any>(child)
                  }
                })}
              </ModalContent>
            </ScrollArea>
          ) : (
            <ModalContent>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  return React.cloneElement<any>(child)
                }
              })}
            </ModalContent>
          )}
          <ModalFooter>
            <ModalClose asChild>
              {tertiaryBtnLabel && (
                <Button
                  attributes={btnAttribute}
                  label={tertiaryBtnLabel}
                  variant={tertiaryBtnVariant}
                  onClick={tertiaryBtnCallback || undefined}
                />
              )}
            </ModalClose>
            {secondaryBtnLabel && (
              <Button
                label={secondaryBtnLabel}
                onClick={handleSecondaryCallback}
                variant="primary"
                state={secondaryBtnState}
              />
            )}
            {primaryBtnLabel && (
              <Button
                disabled={disablePrimaryBtn}
                label={primaryBtnLabel}
                onClick={handlePrimaryCallback}
                variant="secondary"
                state={primaryBtnState}
              />
            )}
          </ModalFooter>
        </ModalContainer>
      </DialogPrimitive.Portal>
    </ModalRoot>
  )
}

const overlayShow = keyframes({
  '0%': { opacity: 0 },
  '100%': { opacity: 1 },
})

const contentShow = keyframes({
  '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(.96)' },
  '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
})

// Stack above Chatbot (z-index 1000); overlay under modal content.
const MODAL_OVERLAY_Z = 1100
const MODAL_CONTENT_Z = 1101

const StyledOverlay = styled(DialogPrimitive.Overlay, {
  backgroundColor: '$b5',
  zIndex: MODAL_OVERLAY_Z,
  position: 'fixed',
  inset: 0,
  pointerEvents: 'none !important',
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${overlayShow.toString()} $fast cubic-bezier(0.16, 1, 0.3, 1)`,
  },
})

const ModalRoot: React.FC<DialogPrimitive.DialogProps> = ({ children, ...props }) => {
  return <DialogPrimitive.Root {...props}>{children}</DialogPrimitive.Root>
}

const ModalHeaderContainer = styled('div', {
  color: '$gs10',
})

const ModalHeader = styled(DialogPrimitive.Title, {
  display: 'flex',
  alignItems: 'center',
  p: '$7',
  [`& ${ModalHeaderContainer.toString()}`]: {
    flex: '1 1 auto',
    overflow: 'hidden',
  },
})

const ModalClose = styled(DialogPrimitive.Close, {
  mx: '$1',
})

const ModalContainer = styled(DialogPrimitive.Content, {
  backgroundColor: '$gs1',
  border: '1px solid $gs10',
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: '450px',
  maxHeight: '85vh',
  display: 'flex',
  zIndex: MODAL_CONTENT_Z,
  flexDirection: 'column',
  '@media (prefers-reduced-motion: no-preference)': {
    animation: `${contentShow.toString()} $fast cubic-bezier(0.16, 1, 0.3, 1)`,
    willChange: 'transform',
  },
  '&:focus': { outline: 'none' },
  variants: {
    size: {
      xs: {
        '@bp1': { maxWidth: 'min(calc(100vw * 0.48), 1200px)' },
        '@bp2': { maxWidth: 'min(calc(100vw * 0.32), 1200px)' },
        '@bp3': { maxWidth: 'min(calc(100vw * 0.24), 1200px)' },
        '@bp4': { maxWidth: 'min(calc(100vw * 0.24), 1200px)' },
        '@initial': { maxWidth: 'min(calc(100vw), 1200px)' },
      },
      small: {
        '@bp1': { maxWidth: 'min(calc(100vw * 0.60), 1200px)' },
        '@bp2': { maxWidth: 'min(calc(100vw * 0.42), 1200px)' },
        '@bp3': { maxWidth: 'min(calc(100vw * 0.36), 1200px)' },
        '@bp4': { maxWidth: 'min(calc(100vw * 0.36), 1200px)' },
        '@initial': { maxWidth: 'min(calc(100vw), 1200px)' },
      },
      default: {
        '@bp1': { maxWidth: 'min(calc(100vw * 0.84), 1200px)' },
        '@bp2': { maxWidth: 'min(calc(100vw * 0.60), 1200px)' },
        '@bp3': { maxWidth: 'min(calc(100vw * 0.48), 1200px)' },
        '@bp4': { maxWidth: 'min(calc(100vw * 0.48), 1200px)' },
        '@initial': { maxWidth: 'min(calc(100vw), 1200px)' },
      },
      large: {
        '@bp1': { maxWidth: 'min(calc(100vw * 0.96), 1200px)' },
        '@bp2': { maxWidth: 'min(calc(100vw * 0.84), 1200px)' },
        '@bp3': { maxWidth: 'min(calc(100vw * 0.72), 1200px)' },
        '@bp4': { maxWidth: 'min(calc(100vw * 0.72), 1200px)' },
        '@initial': { maxWidth: 'min(calc(100vw), 1200px)' },
      },
      xLarge: {
        maxWidth: '95vw',
        width: '95vw',
        maxHeight: '95vh',
      },
    },
  },
})

const ModalContent = styled('div', {
  display: 'block',
  px: '$7',
  overflowY: 'auto',
})

const ModalFooter = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  p: '$7',
  gap: '$3',
})

const ModalTrigger = DialogPrimitive.Trigger
